import SearchBar from '../../../components/Filtering/SearchBar';
import GenerateVisualizationModal from './GenerateVisualizationComponents/GenerateVisualizationModal';
import { useGetDatasetVisualizationsResultsetDatasetVisualizationResultsetIdGet } from '../../../api/generated/fastAPI';
import { ChangeEvent, useState, MouseEvent, useMemo } from 'react';
import CustomCheckbox from '../../../components/Form/CustomCheckbox';
import { useNavigate } from 'react-router-dom';
import MiniVisPlot from './GenerateVisualizationComponents/MiniVisPlot';
import { Skeleton } from '@mui/material';
import NestedDropdown from '../../../components/Menus&Dropdowns/NestedDropdown';
import { DropdownOption } from '../../../components/Menus&Dropdowns/Dropdown';
import { Resultset } from '../../../api/models';
import { exportFileExtensions } from '../../../utils/supplementalData';
import useDownload from '../../../hooks/endpoints/useDownload';
import useColorPalette from '../../../hooks/useColorPalette';


export interface ResultsetVisualizationType {
    graph_data: {
        [key: string]: {
            dataset_hash: string;
            structure_column: string;
            "structure/SMILES": string[];
            x: number[];
            y: number[];
        }
    };
    metadata: VisualizationMetadata;
    test_keys: string[];
    train_keys: string[];
}

export interface VisualizationMetadata {
    creation_date: string;
    updated_date: string;
    file_hash: string;
    dataset_visualization_metadata: {
        airflow_dag: any | null;
        airflow_dag_run_conf: any | null;
        visualization_method_metadata: {
            [key: string]: any | null;
            method_type: string;
            params: { [key: string]: string | number | boolean }
        }
    };
    is_single_prediction_visualization?: boolean;
};

const getParams = (vis: ResultsetVisualizationType) => {
    const fingerprintType = vis.metadata.dataset_visualization_metadata.airflow_dag_run_conf.fp_type;
    const { method_type, params } = vis.metadata.dataset_visualization_metadata.visualization_method_metadata;

    let fields: { label: string, value: any, type: 'text' | 'boolean' }[] = fingerprintType ? [{
        label: 'Fingerprint',
        value: (fingerprintType as string).toUpperCase(),
        type: 'text'
    }] : [];

    if (method_type === 'tsne') {
        fields = fields.concat([
            {
                label: 'Perplexity',
                value: params.perplexity ? params.perplexity : 'No Value',
                type: 'text',
            },
            {
                label: 'Method',
                value: params.method ? params.method : 'No Value',
                type: 'text',
            }
        ]);
    } else if (method_type === 'pca') {
        fields = fields.concat([
            {
                label: 'Whiten',
                value: params.whiten ? params.whiten : 'No Value',
                type: 'boolean'
            },
            {
                label: 'SVD Solver',
                value: params.svd_solver ? params.svd_solver : 'No Value',
                type: 'text'
            }
        ]);
    } else {
        fields = fields.concat([
            {
                label: 'Nearest Neighbors',
                value: params.n_neighbors ? params.n_neighbors : 'No Value',
                type: 'text'
            },
            {
                label: 'Metric',
                value: params.metric ? params.metric : 'No Value',
                type: 'text',
            },
            {
                label: 'Init',
                value: params.init ? params.init : 'No Value',
                type: 'text',
            }
        ])
    };

    return fields;
}

export default function ResultsetVisualizations({
    setPopupMessage,
    resultset,
    modelFamilyOptions,
}: {
    setPopupMessage: (popupMessage: string, isSuccessMessage: boolean) => void,
    resultset: Resultset | null,
    modelFamilyOptions: { name: string, id: number }[],
}) {
    const { palette } = useColorPalette();
    const { data, isLoading } = useGetDatasetVisualizationsResultsetDatasetVisualizationResultsetIdGet(resultset ? resultset.id! : 0);

    const [ selectedItems, setSelectedItems ] = useState<string[]>([]);
    const navigate = useNavigate();
    const { downloadResultsetVisualization } = useDownload();

    const getActionDropdownOptions = (visData: ResultsetVisualizationType[], rs: Resultset | null, selections: string[]) => {
        const options: DropdownOption[] = [];

        if (visData.length > 0 && rs && selections.length > 0) {
            const selectedVisualizations = visData.filter(vis => selections.includes(vis.metadata.file_hash));
            if (selectedVisualizations.length > 0) {
                options.push({
                    label: 'Export',
                    action: (e:MouseEvent<HTMLButtonElement>) => {},
                    moreOptions: (exportFileExtensions.filter(ext => ext.label !== 'SDF')).map(ext => (
                        {
                            label: ext.label,
                            action: async (e: MouseEvent<HTMLButtonElement>) => {
                                e.preventDefault();
                                const promises = selectedVisualizations.map(async (vis) => {
                                    return downloadResultsetVisualization(
                                        ext.file_type,
                                        vis,
                                        rs,
                                    )
                                });
                                try {
                                    await Promise.all(promises);
                                    setSelectedItems([]);
                                    setPopupMessage('You have successfully downloaded the selected visualization.', true);
                                } catch (err:any) {
                                    console.log(err);
                                    setPopupMessage('There was an error downloading this visualization. Please try again.', false);
                                };
                            }
                        }
                    ))
                });
            }

            if (selections.length !== visData.length) {
                options.push({
                    label: 'Select All',
                    action: (e: MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        setSelectedItems(visData.map(vis => vis.metadata.file_hash));
                    }
                });
            }

            options.push({
                label: 'Deselect All',
                action: (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    setSelectedItems([]);
                }
            });
        };

        return options;
    }

    const handleItemSelection = (e:ChangeEvent<HTMLInputElement>) => {
        const id = e.target.value;
        if (e.target.checked) {
            setSelectedItems(selectedItems.concat(id));
        } else {
            setSelectedItems(selectedItems.filter(item => item !== id));
        }
    };


    const visualizations = useMemo(() => {
        if (!data) return [];
        return data as unknown as ResultsetVisualizationType[];
    }, [data]);

    const actionDropdownOptions: DropdownOption[] = useMemo(() => 
        getActionDropdownOptions(visualizations, resultset, selectedItems)

    //eslint-disable-next-line
    ,[visualizations, resultset, selectedItems]);


    return (
        <div className="h-full overflow-hidden flex flex-col justify-start items-stretch gap-4">
        {isLoading ? (
            <>
            <div className='w-full flex justify-between items-stretch h-[44px]'>
                <div className='w-[600px] rounded-lg'><Skeleton variant='rounded' width='100%' height='100%'/></div>
                <div className='w-[220] rounded-lg'><Skeleton variant='rounded' width='100%' height='100%'/></div>
            </div>

            <div className="grow">
                <div className={`w-full data-grid vis pt-6`}>
                    {Array.from(Array(8).keys()).map(k => (
                        <div className="w-full h-[400px] rounded-lg"><Skeleton variant='rounded' width='100%' height='100%'/></div>
                    ))}
                </div>
            </div>
            </>
        ) : visualizations.length > 0 ? (
            <>
                <div className='w-full flex justify-between items-center'>
                    <div className="max-w-[600px] w-full">
                        <SearchBar
                            // searchFn={search}
                        />
                    </div>
                    <div className="flex justify-end items-center gap-4">
                        <GenerateVisualizationModal 
                            setPopupMessage={setPopupMessage}
                            resultsetId={resultset ? resultset.id! : 0}
                        />
                        {selectedItems.length > 0 && (                                
                            <NestedDropdown
                                dir="right" 
                                btn={{
                                    label: `Actions (${selectedItems.length} selected)`,
                                    type: 'secondary',
                                    styling: 'solid',
                                    id: 'resultset-multi-action-btn',
                                }}
                                options={actionDropdownOptions}
                            />
                        )}
                    </div>
                </div>

                <div className="grow">
                    <div className={`w-full data-grid vis pt-6`}>
                        {visualizations.map(visualization => {
                            const id = visualization.metadata.file_hash;
                            const visMethod = visualization.metadata.dataset_visualization_metadata.visualization_method_metadata.method_type;
                            const fields = getParams(visualization);

                            return (
                                <div className={`w-full relative overflow-hidden rounded-xl bg-white border border-outline flex flex-col justify-start items-stretch`}>
                                    <div className="z-20 absolute top-0 left-0 p-7 flex justify-start items-center">
                                        <CustomCheckbox
                                            checked={selectedItems.includes(id)}
                                            handleChange={handleItemSelection}
                                            value={id}
                                        />
                                    </div>
                                    {selectedItems.includes(id) && (
                                        <div className="z-10 absolute top-0 left-0 right-0 bottom-0 bg-[#D9DBE7]/[0.4]"></div>
                                    )}
                                    
                                    <MiniVisPlot 
                                        visualization={visualization}
                                        resultsetName={resultset ? resultset.name! : ''}
                                        modelFamilyOptions={modelFamilyOptions}
                                    />


                                    <div className="w-full grow flex flex-col justify-start items-stretch gap-6 p-6 border-t border-outline">
                                        <div>
                                            <p className={`font-semibold text-default text-[16px] max-w-full truncate uppercase`}>
                                                {visMethod}
                                            </p>
                                        </div>
                                        <div className='grow flex flex-col justify-start items-stretch gap-6'>
                                        {fields.length > 0 && fields.map(field => (
                                            <div className="w-full flex flex-col justify-start items-start gap-2">
                                                <p className="text-[12px] font-semibold text-disable">{field.label}</p>
                                                {field.type === 'text' ? (
                                                     <p className="text-default max-w-full truncate">{field.value}</p>
                                                ) : (
                                                    <div className={`${(field.value as boolean) ? 'bg-[#C7DB78]' : 'bg-warning'} flex justify-center items-center gap-2 py-1 px-3 text-default font-semibold text-[12px] rounded-lg`}>
                                                        {field.value}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        </div>
                                    
                                    </div>

                                    <button onClick={(e:any) => navigate(`/resultsets/${resultset ? resultset.id! : 0}/visualization/${id}`)} className="w-full bg-[#F7F6FB] text-disable font-semibold text-[12px] p-3 hover:bg-secondary hover:text-primary transition-all">
                                        See more details
                                    </button>
                                </div>
                            )
                        })}
                    </div>
                </div>

            </>
        ) : (
            <div className="w-full flex flex-col justify-center items-center p-10 gap-4">
                <img src={`${process.env.PUBLIC_URL}/no-docs/${palette.logo}`} className="w-[160px]" alt='no-visualization-results' />
                <h2 className="text-[24px] font-semibold">No Visualization Yet</h2>
                <p className="max-w-[600px] text-center text-default mb-4">There are no visualizations associated with this resultset. <br/> To generate a new visualization, click on the button below.</p>
                <GenerateVisualizationModal 
                    setPopupMessage={setPopupMessage}
                    resultsetId={resultset ? resultset.id! : 0}
                />
            </div>
        )}  
        </div>
    )
}